<app-order-filter #filter
  (filterChanged)="onFilterChanged($event)"
  (userFilter)="onUserFilterChanged($event)"
  [additional]="additionalFields$ | async"
  [couriers$]="couriers$"
  [goods$]="goods$"
  [senders$]="senders$"
  [statuses$]="statuses$"
  [storehouses$]="storehouses$"
  [fieldMap]="orderFieldMap$ | async"
  [key]="ORDER_PAGE_KEY"
  absolute>
</app-order-filter>

<app-layout preset="data">
  <app-layout-title align="start">
    @if ((loading$ | async) !== true) {
      <form [formGroup]="filter.form">
        <app-toggle-group (toggled)="filter.reload()"
          [toggleItems]="selectedStatuses$ | async"
          formControlName="statuses"
          style="font-weight: normal"
          syncControl>
        </app-toggle-group>
      </form>
    } @else {
      <ngx-skeleton-loader [count]="5"
        [theme]="{ height: '30px', width: '150px', marginRight: '5px' }">
      </ngx-skeleton-loader>
    }
  </app-layout-title>

  <app-layout-actions align="start">
    <div class="actions-alignment">
      <div class="top-row">
        <button (click)="openCreateOrderDialog()" color="primary" mat-button>
          <mat-label>{{ 'ORDERS.IMPORT.add_orders' | translate }}</mat-label>
          <mat-icon>add</mat-icon>
        </button>

        <app-order-actions [orderCount]="selectedOrderCount"
          [backLink]="['orders']"
          [filter]="procedureFilter">
        </app-order-actions>
      </div>

      <div class="bottom-row">
        <app-column-select #col
          [columns]="columns"
          class="column-select"
          storageKey="new-order-table">
        </app-column-select>
      </div>
    </div>
  </app-layout-actions>

  <app-layout-content>
    <app-table #table
      (selectionChanged)="onOrdersSelected($event)"
      (totalCountChanged)="onOrderCountChanged($event)"
      (pageChanged)="updatePageData($event.pageIndex, $event.pageSize)"
      [filter]="filter"
      [pageIndex]="pageIndex"
      [limit]="pageSize"
      [columns]="col.activeColumns"
      [dataSource]="dataSource"
      [manualLoading]="true"
      [selectionResetter]="selectionResetter"
      [withSelection]="true">

      <app-table-row [rowClasses]="rowClasses"></app-table-row>

      <app-sorting-popover *app-table-header="'id'"
        [(ordering)]="table.ordering"
        columnName="ID">
        <app-popover-sort field="lgStandingData.outOrderId" [label]="'ORDERS.COMMON.ORDER_ID.order_id_tooltip' | translate"></app-popover-sort>
        <app-popover-sort field="lgStandingData.outProjectId" [label]="'ORDERS.COMMON.ORDER_ID.project_id_tooltip' | translate"></app-popover-sort>

        <form [formGroup]="filter.form">
          <div style="display: grid">
            <app-separator-input formControlName="outProjectId" syncControl>
              <app-separator-label>{{ 'ORDERS.FILTERS.search_projects' | translate }}</app-separator-label>
            </app-separator-input>

            <app-separator-input formControlName="outOrderId" syncControl>
              <app-separator-label>{{ 'ORDERS.FILTERS.search_orders' | translate }}</app-separator-label>
            </app-separator-input>

            <div class="buttons">
              <button (click)="filter.reset('outProjectId', 'outOrderId')" mat-icon-button color="warn" type="button">
                <mat-icon>close</mat-icon>
              </button>
              <button (click)="filter.reload()" color="primary" mat-icon-button type="submit">
                <mat-icon>search</mat-icon>
              </button>
            </div>
          </div>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'cart'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.cart' | translate">
        <app-popover-sort field="cart.totalQuantity" [label]="'ORDERS.COMMON.CART.total_quantity_tooltip' | translate"></app-popover-sort>

        <form [formGroup]="filter.form">
          <app-select formControlName="goods" multiple syncControl>
            <app-select-label>{{ 'ORDERS.FILTERS.search_goods' | translate }}</app-select-label>
            @for (goods of goods$ | async; track goods) {
              <mat-option [value]="goods.id">{{ goods.name }}</mat-option>
            }
          </app-select>

          <div class="buttons">
            <button (click)="filter.reset('goods')" color="warn" mat-icon-button type="button">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" color="primary" mat-icon-button type="submit">
              <mat-icon>search</mat-icon>
            </button>
          </div>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'recipient'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.recipient' | translate">
        <form [formGroup]="filter.form">
          <div style="display: grid">
            <app-separator-input formControlName="phone" syncControl>
              <app-separator-label>{{ 'ORDERS.FILTERS.phones' | translate }}</app-separator-label>
            </app-separator-input>

            <app-separator-input formControlName="postcode" syncControl>
              <app-separator-label>{{ 'ORDERS.FILTERS.postcodes' | translate }}</app-separator-label>
            </app-separator-input>
          </div>

          <div class="buttons">
            <button (click)="filter.reset('phone', 'postcode')" color="warn" mat-icon-button type="button">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" color="primary" mat-icon-button type="submit">
              <mat-icon>search</mat-icon>
            </button>
          </div>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'dates'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.dates' | translate">
        <app-popover-sort field="createdAt" [label]="'ORDERS.COMMON.DATES.created_at_tooltip' | translate"></app-popover-sort>
        <app-popover-sort field="updatedAt" [label]="'ORDERS.COMMON.DATES.updated_at_tooltip' | translate"></app-popover-sort>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'courier'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.courier' | translate">
        <app-popover-sort field="logistic.track" [label]="'ORDERS.COMMON.UNLOADING.track_number_tooltip' | translate"></app-popover-sort>
        <app-popover-sort field="logistic.courier" [label]="'ORDERS.COMMON.UNLOADING.courier_tooltip' | translate"></app-popover-sort>
        <app-popover-sort field="logistic.sender" [label]="'ORDERS.COMMON.UNLOADING.sender_tooltip' | translate"></app-popover-sort>

        <form [formGroup]="filter.form">
          <app-separator-input formControlName="track" syncControl>
            <app-separator-label>{{ 'ORDERS.FILTERS.search_tracks' | translate }}</app-separator-label>
          </app-separator-input>

          <div class="buttons">
            <button (click)="filter.reset('track')" color="warn" mat-icon-button type="button">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" color="primary" mat-icon-button>
              <mat-icon>search</mat-icon>
            </button>
          </div>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'tracking'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.tracking' | translate">
        <form [formGroup]="filter.form">
          <app-separator-input formControlName="tracking" syncControl>
            <app-separator-label>{{ 'ORDERS.FILTERS.search_tracking_statuses' | translate }}</app-separator-label>
          </app-separator-input>

          <div class="buttons">
            <button (click)="filter.reset('tracking')" color="warn" mat-icon-button type="button">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" color="primary" mat-icon-button type="submit">
              <mat-icon>search</mat-icon>
            </button>
          </div>
          <app-popover-sort field="logistic.tracking.lastName" [label]="'ORDERS.FILTERS.tracking_status' | translate"></app-popover-sort>
          <app-popover-sort field="logistic.tracking.lastAssignedAt" [label]="'ORDERS.COMMON.TRACKING.assigned_at' | translate"></app-popover-sort>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'status'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.status' | translate">
        <app-popover-sort field="status.name" [label]="'ORDERS.FILTERS.status_name' | translate"></app-popover-sort>
        <app-popover-sort field="statusChangeAt" [label]="'ORDERS.DETAILS.status_date' | translate"></app-popover-sort>
      </app-sorting-popover>

      <ng-container *app-table-header="'store'">{{ 'ORDER_PAGE.TABLE.store' | translate }}</ng-container>

      <app-sorting-popover *app-table-header="'comment'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.comment' | translate">
        <form [formGroup]="filter.form">
          <mat-form-field class="popover-grid">
            <mat-label>{{ 'ORDERS.FILTERS.search_comment' | translate }}</mat-label>
            <input formControlName="comment" syncControl matInput [placeholder]="'ORDERS.FILTERS.enter_comment' | translate">
          </mat-form-field>
          <div class="buttons">
            <button (click)="filter.reset('comment')" mat-icon-button type="button" color="warn">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" mat-icon-button color="primary">
              <mat-icon>search</mat-icon>
            </button>
          </div>
        </form>
      </app-sorting-popover>

      <app-sorting-popover *app-table-header="'additions'"
        [(ordering)]="table.ordering"
        [columnName]="'ORDER_PAGE.TABLE.additions' | translate">
        <form [formGroup]="filter.form">
          <app-additional-fields-search #additionsGroup="control"
            [additionalFields]="additionalFields$ | async"
            formGroupName="additions">
          </app-additional-fields-search>
          <div class="buttons">
            <button (click)="additionsGroup.reset(); filter.reload()" mat-icon-button type="button" color="warn">
              <mat-icon>close</mat-icon>
            </button>
            <button (click)="filter.reload()" mat-icon-button color="primary">
              <mat-icon>search</mat-icon>
            </button>
          </div>
        </form>
      </app-sorting-popover>

      <app-order-columns-container [orderLink]="['orders']"
        [columns]="columnNames"
        [tableLoading]="table.loading">
      </app-order-columns-container>
    </app-table>

  </app-layout-content>
</app-layout>

<router-outlet></router-outlet>
